/**
 * Resume section editors — the "add / edit client info" components of the Resume Builder.
 *
 * Every editor is controlled: it receives its slice of the resume and an onChange that
 * returns the new slice. List sections share the same controls (add, move up/down,
 * remove) so experience, education, volunteer, etc. behave identically.
 *
 * Content shape mirrors the server's ClientResume (see maven-platform-server
 * src/utils/resumeContent.ts) — keep them in sync.
 */
import React, { useState } from 'react';
import {
  Card, CardHead, CardBody, Entry, EntryHead, Row, Field, Label, Hint, Input, TextArea,
  Btn, IconBtn, Chip, Check,
} from './resumeStyles';

// ---- list helpers ----------------------------------------------------------
const updateAt = (list, i, patch) => list.map((x, k) => (k === i ? { ...x, ...patch } : x));
const removeAt = (list, i) => list.filter((_, k) => k !== i);
const move = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export const EMPTY_EXPERIENCE = { jobTitle: '', employer: '', city: '', region: '', country: '', startDate: '', endDate: '', current: false, bullets: [] };
export const EMPTY_EDUCATION = { credential: '', fieldOfStudy: '', institution: '', city: '', country: '', startDate: '', endDate: '', current: false, canadianEquivalency: '', details: [] };

const PROFICIENCY_OPTIONS = ['Native', 'Fluent', 'Advanced', 'Intermediate', 'Basic', 'CLB 9+', 'CLB 7', 'CLB 5', 'NCLC 7'];
const DATE_HINT = 'YYYY-MM or YYYY';

// ---- building blocks -------------------------------------------------------

export function Section({ title, count, defaultOpen = true, hint, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <CardHead type="button" $open={open} onClick={() => setOpen(o => !o)}>
        {title}
        {count !== undefined && <span className="count">{count}</span>}
        <span className="chev">▶</span>
      </CardHead>
      {open && (
        <CardBody>
          {hint && <Hint $mb>{hint}</Hint>}
          {children}
        </CardBody>
      )}
    </Card>
  );
}

function EntryControls({ index, total, onMove, onRemove, label }) {
  return (
    <EntryHead>
      <span className="label">{label}</span>
      <IconBtn type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}>↑</IconBtn>
      <IconBtn type="button" title="Move down" disabled={index === total - 1} onClick={() => onMove(1)}>↓</IconBtn>
      <IconBtn type="button" title="Remove" $danger onClick={onRemove}>×</IconBtn>
    </EntryHead>
  );
}

/** Ordered list of one-line bullets (achievements / details). */
export function BulletsEditor({ items = [], onChange, placeholder = 'Start with an action verb, e.g. "Maintained…"', addLabel = 'Add bullet' }) {
  const set = (i, v) => onChange(items.map((x, k) => (k === i ? v : x)));
  return (
    <div>
      {items.map((b, i) => (
        <Row key={i} style={{ marginBottom: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
          <span style={{ color: '#8b5cf6', fontSize: 14 }}>•</span>
          <Input
            value={b}
            placeholder={placeholder}
            onChange={e => set(i, e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); onChange([...items.slice(0, i + 1), '', ...items.slice(i + 1)]); }
            }}
          />
          <IconBtn type="button" title="Move up" disabled={i === 0} onClick={() => onChange(move(items, i, -1))}>↑</IconBtn>
          <IconBtn type="button" title="Move down" disabled={i === items.length - 1} onClick={() => onChange(move(items, i, 1))}>↓</IconBtn>
          <IconBtn type="button" title="Remove" $danger onClick={() => onChange(removeAt(items, i))}>×</IconBtn>
        </Row>
      ))}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([...items, ''])}>+ {addLabel}</Btn>
    </div>
  );
}

/** Tag-style input: Enter or comma adds; pasting a comma/newline list adds each item. */
export function ChipsInput({ items = [], onChange, placeholder = 'Type and press Enter' }) {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const parts = String(raw).split(/[,\n;]/).map(s => s.trim()).filter(Boolean);
    const next = [...items];
    for (const p of parts) if (!next.some(x => x.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setDraft('');
  };
  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {items.map((s, i) => (
            <Chip key={`${s}-${i}`}>{s}<button type="button" title="Remove" onClick={() => onChange(removeAt(items, i))}>×</button></Chip>
          ))}
        </div>
      )}
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ',') && draft.trim()) { e.preventDefault(); add(draft); }
          if (e.key === 'Backspace' && !draft && items.length) onChange(items.slice(0, -1));
        }}
        onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (/[,\n;]/.test(text)) { e.preventDefault(); add(text); }
        }}
        onBlur={() => draft.trim() && add(draft)}
      />
    </div>
  );
}

// ---- sections --------------------------------------------------------------

export function ContactEditor({ contact = {}, onChange }) {
  const f = (key, label, props = {}) => (
    <Field $min={props.min || '160px'} $grow={props.grow}>
      <Label>{label}</Label>
      <Input value={contact[key] || ''} placeholder={props.placeholder} onChange={e => onChange({ ...contact, [key]: e.target.value })} />
    </Field>
  );
  return (
    <>
      <Row>{f('fullName', 'Full name', { grow: 1.3 })}{f('headline', 'Headline / target role', { grow: 1.7, placeholder: 'e.g. Licensed Electrician (309A)' })}</Row>
      <Row $mt>{f('email', 'Email')}{f('phone', 'Phone')}</Row>
      <Row $mt>{f('city', 'City')}{f('region', 'Province / State', { min: '120px' })}{f('country', 'Country', { min: '120px' })}</Row>
      <Row $mt>{f('linkedin', 'LinkedIn', { placeholder: 'linkedin.com/in/…' })}{f('website', 'Website / portfolio')}</Row>
      <Row $mt>{f('workAuthorization', 'Work authorization (optional)', { placeholder: 'e.g. Permanent Resident · Open Work Permit valid to 2027-05' })}</Row>
    </>
  );
}

export function SkillsEditor({ groups = [], onChange }) {
  return (
    <>
      {groups.map((g, i) => (
        <Entry key={i}>
          <EntryControls
            index={i} total={groups.length} label={g.label || 'Skills (no label — shown as a bullet list)'}
            onMove={d => onChange(move(groups, i, d))}
            onRemove={() => onChange(removeAt(groups, i))}
          />
          <Row>
            <Field>
              <Label>Group label (optional)</Label>
              <Input value={g.label || ''} placeholder="e.g. Technical Skills" onChange={e => onChange(updateAt(groups, i, { label: e.target.value }))} />
            </Field>
          </Row>
          <div style={{ marginTop: 10 }}>
            <ChipsInput items={g.items || []} onChange={items => onChange(updateAt(groups, i, { items }))} placeholder="Add a skill and press Enter (paste a comma list to add many)" />
          </div>
        </Entry>
      ))}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([...groups, { label: '', items: [] }])}>+ Add skill group</Btn>
    </>
  );
}

function DateFields({ value, onChange, allowCurrent = true, currentLabel = 'I currently work here' }) {
  return (
    <Row $mt style={{ alignItems: 'flex-end' }}>
      <Field $min="120px"><Label>Start</Label>
        <Input value={value.startDate || ''} placeholder={DATE_HINT} onChange={e => onChange({ startDate: e.target.value })} />
      </Field>
      <Field $min="120px"><Label>End</Label>
        <Input value={value.current ? '' : value.endDate || ''} disabled={!!value.current} placeholder={value.current ? 'Present' : DATE_HINT} onChange={e => onChange({ endDate: e.target.value })} />
      </Field>
      {allowCurrent && (
        <Field $min="150px" style={{ paddingBottom: 8 }}>
          <Check><input type="checkbox" checked={!!value.current} onChange={e => onChange({ current: e.target.checked, endDate: e.target.checked ? '' : value.endDate })} />{currentLabel}</Check>
        </Field>
      )}
    </Row>
  );
}

/** Used for both Professional Experience and Volunteer Experience. */
export function ExperienceEditor({ items = [], onChange, volunteer = false }) {
  const titleLabel = volunteer ? 'Role' : 'Job title';
  const orgLabel = volunteer ? 'Organization' : 'Employer';
  return (
    <>
      {items.map((e, i) => {
        const patch = p => onChange(updateAt(items, i, p));
        return (
          <Entry key={i}>
            <EntryControls
              index={i} total={items.length}
              label={[e.jobTitle, e.employer].filter(Boolean).join(' — ') || `${volunteer ? 'Volunteer role' : 'Position'} ${i + 1}`}
              onMove={d => onChange(move(items, i, d))}
              onRemove={() => onChange(removeAt(items, i))}
            />
            <Row>
              <Field><Label>{titleLabel}</Label><Input value={e.jobTitle || ''} onChange={ev => patch({ jobTitle: ev.target.value })} /></Field>
              <Field><Label>{orgLabel}</Label><Input value={e.employer || ''} onChange={ev => patch({ employer: ev.target.value })} /></Field>
            </Row>
            <Row $mt>
              <Field><Label>City</Label><Input value={e.city || ''} onChange={ev => patch({ city: ev.target.value })} /></Field>
              <Field $min="110px"><Label>Province / State</Label><Input value={e.region || ''} onChange={ev => patch({ region: ev.target.value })} /></Field>
              <Field $min="110px"><Label>Country</Label><Input value={e.country || ''} onChange={ev => patch({ country: ev.target.value })} /></Field>
            </Row>
            <DateFields value={e} onChange={patch} currentLabel={volunteer ? 'Ongoing' : 'I currently work here'} />
            <div style={{ marginTop: 10 }}>
              <Label>{volunteer ? 'What they did' : 'Achievements & responsibilities'}</Label>
              <div style={{ marginTop: 6 }}><BulletsEditor items={e.bullets || []} onChange={bullets => patch({ bullets })} /></div>
            </div>
          </Entry>
        );
      })}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([{ ...EMPTY_EXPERIENCE }, ...items])}>
        + Add {volunteer ? 'volunteer role' : 'position'} (at the top — most recent first)
      </Btn>
    </>
  );
}

export function EducationEditor({ items = [], onChange }) {
  return (
    <>
      {items.map((e, i) => {
        const patch = p => onChange(updateAt(items, i, p));
        return (
          <Entry key={i}>
            <EntryControls
              index={i} total={items.length}
              label={[e.credential, e.institution].filter(Boolean).join(' — ') || `Education ${i + 1}`}
              onMove={d => onChange(move(items, i, d))}
              onRemove={() => onChange(removeAt(items, i))}
            />
            <Row>
              <Field><Label>Credential</Label><Input value={e.credential || ''} placeholder="e.g. Bachelor of Science, Diploma" onChange={ev => patch({ credential: ev.target.value })} /></Field>
              <Field><Label>Field of study</Label><Input value={e.fieldOfStudy || ''} onChange={ev => patch({ fieldOfStudy: ev.target.value })} /></Field>
            </Row>
            <Row $mt>
              <Field $grow={2}><Label>Institution</Label><Input value={e.institution || ''} onChange={ev => patch({ institution: ev.target.value })} /></Field>
              <Field><Label>City</Label><Input value={e.city || ''} onChange={ev => patch({ city: ev.target.value })} /></Field>
              <Field $min="110px"><Label>Country</Label><Input value={e.country || ''} onChange={ev => patch({ country: ev.target.value })} /></Field>
            </Row>
            <DateFields value={e} onChange={patch} currentLabel="Currently studying" />
            <Row $mt>
              <Field>
                <Label>Canadian equivalency (optional)</Label>
                <Input value={e.canadianEquivalency || ''} placeholder="e.g. WES ECA: equivalent to a Canadian bachelor's degree" onChange={ev => patch({ canadianEquivalency: ev.target.value })} />
              </Field>
            </Row>
            <div style={{ marginTop: 10 }}>
              <Label>Details (optional)</Label>
              <div style={{ marginTop: 6 }}><BulletsEditor items={e.details || []} onChange={details => patch({ details })} placeholder="e.g. Graduated with honours" addLabel="Add detail" /></div>
            </div>
          </Entry>
        );
      })}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([{ ...EMPTY_EDUCATION }, ...items])}>+ Add education (at the top — most recent first)</Btn>
    </>
  );
}

export function CertificationsEditor({ items = [], onChange }) {
  return (
    <>
      {items.map((c, i) => {
        const patch = p => onChange(updateAt(items, i, p));
        return (
          <Entry key={i}>
            <EntryControls index={i} total={items.length} label={c.name || `Certification ${i + 1}`}
              onMove={d => onChange(move(items, i, d))} onRemove={() => onChange(removeAt(items, i))} />
            <Row>
              <Field $grow={2}><Label>Name</Label><Input value={c.name || ''} placeholder="e.g. Certificate of Qualification — 309A" onChange={ev => patch({ name: ev.target.value })} /></Field>
              <Field><Label>Issuer</Label><Input value={c.issuer || ''} onChange={ev => patch({ issuer: ev.target.value })} /></Field>
            </Row>
            <Row $mt>
              <Field $min="120px"><Label>Date obtained</Label><Input value={c.date || ''} placeholder={DATE_HINT} onChange={ev => patch({ date: ev.target.value })} /></Field>
              <Field $min="120px"><Label>Expiry</Label><Input value={c.expiry || ''} placeholder={DATE_HINT} onChange={ev => patch({ expiry: ev.target.value })} /></Field>
              <Field $min="120px"><Label>Licence #</Label><Input value={c.licenseNumber || ''} onChange={ev => patch({ licenseNumber: ev.target.value })} /></Field>
            </Row>
          </Entry>
        );
      })}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([...items, { name: '', issuer: '', date: '', expiry: '', licenseNumber: '' }])}>+ Add certification / licence</Btn>
    </>
  );
}

export function LanguagesEditor({ items = [], onChange }) {
  return (
    <>
      <datalist id="resume-proficiency-options">{PROFICIENCY_OPTIONS.map(o => <option key={o} value={o} />)}</datalist>
      {items.map((l, i) => (
        <Row key={i} style={{ marginBottom: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
          <Input value={l.language || ''} placeholder="Language" onChange={e => onChange(updateAt(items, i, { language: e.target.value }))} />
          <Input value={l.proficiency || ''} placeholder="Proficiency (e.g. Fluent, CLB 7)" list="resume-proficiency-options" onChange={e => onChange(updateAt(items, i, { proficiency: e.target.value }))} />
          <IconBtn type="button" title="Move up" disabled={i === 0} onClick={() => onChange(move(items, i, -1))}>↑</IconBtn>
          <IconBtn type="button" title="Remove" $danger onClick={() => onChange(removeAt(items, i))}>×</IconBtn>
        </Row>
      ))}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([...items, { language: '', proficiency: '' }])}>+ Add language</Btn>
    </>
  );
}

export function AdditionalEditor({ items = [], onChange }) {
  return (
    <>
      {items.map((s, i) => (
        <Entry key={i}>
          <EntryControls index={i} total={items.length} label={s.heading || `Section ${i + 1}`}
            onMove={d => onChange(move(items, i, d))} onRemove={() => onChange(removeAt(items, i))} />
          <Field><Label>Heading</Label><Input value={s.heading || ''} placeholder="e.g. Driver's Licence, Equipment Operated, Awards" onChange={e => onChange(updateAt(items, i, { heading: e.target.value }))} /></Field>
          <div style={{ marginTop: 10 }}>
            <BulletsEditor items={s.items || []} onChange={v => onChange(updateAt(items, i, { items: v }))} placeholder="Item" addLabel="Add item" />
          </div>
        </Entry>
      ))}
      <Btn type="button" $sm $variant="ghost" onClick={() => onChange([...items, { heading: '', items: [''] }])}>+ Add custom section</Btn>
    </>
  );
}

export function SummaryEditor({ value = '', onChange }) {
  const words = value.trim() ? value.trim().split(/\s+/).length : 0;
  return (
    <>
      <TextArea $h={110} value={value} onChange={e => onChange(e.target.value)}
        placeholder="2–4 sentences: years of experience, key strengths, and the role the client is targeting. No “I”." />
      <Hint style={{ marginTop: 6, color: words > 110 ? '#fcd34d' : undefined }}>{words} words{words > 110 ? ' — Canadian summaries are usually under ~100 words' : ''}</Hint>
    </>
  );
}
